"use client";

import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { Logo } from "@/components/logo/Logo";
import { NAV_ITEMS, SECONDARY_NAV_ITEMS, type NavItem } from "@/lib/site-data";
import { TopBar } from "@/components/layout/TopBar";
import { publicUrl } from "@/lib/content/path";

interface MenuDropdownPanelProps {
  item: NavItem;
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

function MenuDropdownPanel({
  item,
  onClose,
  onMouseEnter,
  onMouseLeave,
}: MenuDropdownPanelProps) {
  const childCount = item.children?.length ?? 0;
  const isLargeMenu = childCount > 10;
  const dropdownWidth = isLargeMenu
    ? "w-[min(880px,calc(100vw-2rem))] xl:w-[min(1080px,calc(100vw-2rem))]"
    : "w-[min(640px,calc(100vw-2rem))] xl:w-[min(780px,calc(100vw-2rem))]";
  const dropdownGrid = isLargeMenu
    ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
    : "grid-cols-1 md:grid-cols-2";

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`overflow-hidden rounded-[1.75rem] bg-white shadow-2xl ring-1 ring-brand-black/10 transition-all duration-150 ${dropdownWidth}`}
    >
      <div className="flex max-h-[68vh] flex-row overflow-x-hidden overflow-y-auto">
        <div className="flex-1 p-5 sm:p-6">
          <div className="mb-3 border-b border-brand-gray/10 pb-2.5">
            <div>
              <p className="text-lg font-semibold text-brand-black">{item.label}</p>
              <div className="mt-1.5 h-[3px] w-10 rounded-full bg-brand-orange" />
            </div>
          </div>

          <div className={`grid ${dropdownGrid} gap-x-6 gap-y-1`}>
            {item.children?.map((child) => (
              <Link
                key={`${item.label}:${child.label}`}
                href={publicUrl(child.href)}
                onClick={onClose}
                className="group block rounded-xl px-2.5 py-1.5 transition-all hover:bg-brand-orange/5 hover:shadow-sm"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-semibold leading-snug text-brand-black transition-colors group-hover:text-brand-orange">
                    {child.label}
                  </span>
                  <span className="mt-1 block h-[2px] w-8 rounded-full bg-brand-orange/25 transition-all group-hover:w-14 group-hover:bg-brand-orange" />
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="hidden w-[280px] shrink-0 border-l border-brand-gray/10 p-3 lg:block xl:w-[320px]">
          {item.image ? (
            <div className="relative h-full min-h-[170px] w-full overflow-hidden rounded-xl shadow-sm ring-1 ring-black/5">
              <Image
                src={item.image}
                alt={`${item.label} illustration`}
                fill
                sizes="(max-width: 1200px) 280px, 320px"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>
          ) : (
            <div className="flex h-full min-h-[170px] items-start justify-center rounded-xl bg-white/70 p-3 shadow-sm ring-1 ring-white/80 bg-gradient-to-br from-brand-peach/20 via-brand-gray-light to-brand-orange/10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-orange text-xl font-semibold text-white">
                {item.label.charAt(0)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function Header() {
  const [navItems, setNavItems] = useState(NAV_ITEMS);
  const [secondaryNavItems, setSecondaryNavItems] = useState(SECONDARY_NAV_ITEMS);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState<string | null>(null);

  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearCloseTimer = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = (dropdownKey: string, hasChildren: boolean) => {
    clearCloseTimer();
    if (hasChildren) {
      setOpenDropdown(dropdownKey);
    } else {
      closeTimeoutRef.current = setTimeout(() => {
        setOpenDropdown(null);
      }, 150);
    }
  };

  const handleMouseLeave = () => {
    clearCloseTimer();
    closeTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  const handleDropdownMouseEnter = () => {
    clearCloseTimer();
  };

  useEffect(() => {
    return () => {
      clearCloseTimer();
    };
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/public/menu", { signal: controller.signal })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("Menu request failed")))
      .then((data: { items?: typeof NAV_ITEMS; secondaryItems?: typeof SECONDARY_NAV_ITEMS }) => {
        if (data.items?.length) setNavItems(data.items);
        if (data.secondaryItems?.length) setSecondaryNavItems(data.secondaryItems);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  const primaryActiveItem = openDropdown?.startsWith("primary:")
    ? navItems.find((item) => `primary:${item.label}` === openDropdown && item.children?.length)
    : null;

  const secondaryActiveItem = openDropdown?.startsWith("secondary:")
    ? secondaryNavItems.find((item) => `secondary:${item.label}` === openDropdown && item.children?.length)
    : null;

  return (
    <header className={`fixed top-0 inset-x-0 z-50 ${mobileOpen ? "bottom-0 flex flex-col" : ""}`}>
      <motion.div
        animate={{ height: scrolled ? 0 : "auto", opacity: scrolled ? 0 : 1 }}
        transition={{ duration: 0.25, ease: "easeInOut" }}
        className="overflow-hidden"
      >
        <TopBar />
      </motion.div>

      <div
        className={`border-b transition-all duration-300 ${scrolled
            ? "bg-white/90 backdrop-blur-md border-transparent shadow-[0_2px_20px_rgba(0,0,0,0.06)]"
            : "bg-white border-black/5"
          } ${mobileOpen ? "flex-1 flex flex-col min-h-0 bg-white" : ""}`}
      >
        {/* Primary Navigation Row */}
        <div className="w-full px-6 lg:px-12 xl:px-[170px] relative z-30">
          <div className="flex items-center justify-between py-3">
            <Link href="/" className="flex items-center shrink-0">
              <Logo className="h-9 w-auto sm:h-10 lg:h-11" />
            </Link>

            <nav
              className="hidden lg:flex items-center gap-1"
              onMouseLeave={handleMouseLeave}
            >
              {navItems.map((item) => {
                const hasChildren = Boolean(item.children?.length);
                const isOpen = openDropdown === `primary:${item.label}`;
                return (
                  <div
                    key={item.label}
                    onMouseEnter={() => handleMouseEnter(`primary:${item.label}`, hasChildren)}
                    className="relative"
                  >
                    <Link
                      href={publicUrl(item.href)}
                      className={`flex items-center gap-1.5 px-4 py-2 text-[15px] font-medium transition-colors ${
                        isOpen
                          ? "text-brand-orange"
                          : "text-brand-black/80 hover:text-brand-orange"
                      }`}
                    >
                      {item.label}
                      {hasChildren && (
                        <ChevronDown
                          className={`h-3.5 w-3.5 transition-transform duration-200 ${
                            isOpen ? "rotate-180 text-brand-orange" : "text-brand-black/45"
                          }`}
                          aria-hidden="true"
                        />
                      )}
                    </Link>
                  </div>
                );
              })}
            </nav>

            <div className="hidden lg:flex items-center shrink-0">
              <Link
                href="/contact-us"
                data-lead-popup
                className="whitespace-nowrap rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-orange-deep hover:shadow-md transition-all"
              >
                Book Appointment
              </Link>
            </div>

            <button
              aria-label="Toggle menu"
              className="lg:hidden flex flex-col justify-center gap-1.5 h-10 w-10"
              onClick={() => {
                setMobileOpen((o) => {
                  if (o) setMobileOpenDropdown(null);
                  return !o;
                });
              }}
            >
              <motion.span
                animate={{ rotate: mobileOpen ? 45 : 0, y: mobileOpen ? 6 : 0 }}
                className="block h-0.5 w-6 bg-brand-black rounded-full origin-center"
              />
              <motion.span
                animate={{ opacity: mobileOpen ? 0 : 1 }}
                className="block h-0.5 w-6 bg-brand-black rounded-full"
              />
              <motion.span
                animate={{ rotate: mobileOpen ? -45 : 0, y: mobileOpen ? -6 : 0 }}
                className="block h-0.5 w-6 bg-brand-black rounded-full origin-center"
              />
            </button>
          </div>

          {/* Shared Primary Dropdown Overlay */}
          <AnimatePresence>
            {primaryActiveItem && (
              <motion.div
                key="primary-dropdown-container"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4, transition: { duration: 0.12 } }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                className="absolute top-full left-0 right-0 w-full z-50 flex justify-center pt-2 pb-6"
                onMouseEnter={handleDropdownMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <MenuDropdownPanel
                  item={primaryActiveItem}
                  onClose={() => setOpenDropdown(null)}
                  onMouseEnter={handleDropdownMouseEnter}
                  onMouseLeave={handleMouseLeave}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Sub-header / Secondary Navigation Row */}
        <nav
          className="hidden border-t border-white/10 bg-brand-black lg:block relative z-20"
          onMouseLeave={handleMouseLeave}
        >
          <div className="flex w-full justify-center px-6 lg:px-12 xl:px-[170px]">
            {secondaryNavItems.map((item) => {
              const hasChildren = Boolean(item.children?.length);
              const isOpen = openDropdown === `secondary:${item.label}`;
              return (
                <div
                  key={item.label}
                  onMouseEnter={() => handleMouseEnter(`secondary:${item.label}`, hasChildren)}
                >
                  <Link
                    href={publicUrl(item.href)}
                    className={`flex items-center gap-1.5 px-4 py-4 text-sm font-medium transition-colors ${
                      isOpen
                        ? "bg-white/15 text-white shadow-inner"
                        : "text-white/90 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    {item.label}
                    {hasChildren && (
                      <ChevronDown
                        className={`h-3.5 w-3.5 transition-transform duration-200 ${
                          isOpen ? "rotate-180 text-brand-orange-light" : "text-white/70"
                        }`}
                        aria-hidden="true"
                      />
                    )}
                  </Link>
                </div>
              );
            })}
          </div>

          {/* Shared Secondary Dropdown Overlay */}
          <AnimatePresence>
            {secondaryActiveItem && (
              <motion.div
                key="secondary-dropdown-container"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4, transition: { duration: 0.12 } }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                className="absolute top-full left-0 right-0 w-full z-40 flex justify-center pt-2 pb-6"
                onMouseEnter={handleDropdownMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <MenuDropdownPanel
                  item={secondaryActiveItem}
                  onClose={() => setOpenDropdown(null)}
                  onMouseEnter={handleDropdownMouseEnter}
                  onMouseLeave={handleMouseLeave}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </nav>

        {/* Mobile Navigation Drawer */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden flex-1 flex flex-col min-h-0 bg-white border-t border-brand-gray-light"
            >
              <div className="flex-1 overflow-y-auto overscroll-contain pb-10">
                <nav className="flex flex-col p-5 gap-1">
                  {[...navItems, ...secondaryNavItems].map((item) => {
                    if (item.children && item.children.length > 0) {
                      return (
                        <div key={item.label} className="border-b border-brand-gray-light/70 last:border-0">
                          <button
                            onClick={() => setMobileOpenDropdown((current) => (current === item.label ? null : item.label))}
                            className="w-full flex items-center justify-between px-3 py-3 text-base font-medium text-brand-black cursor-pointer focus:outline-none"
                          >
                            <span>{item.label}</span>
                            <motion.svg
                              animate={{ rotate: mobileOpenDropdown === item.label ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                              className="w-4 h-4 text-brand-black/50"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                            </motion.svg>
                          </button>
                          <AnimatePresence initial={false}>
                            {mobileOpenDropdown === item.label && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2, ease: "easeInOut" }}
                                className="overflow-hidden flex flex-col pl-4 gap-1 pb-2"
                              >
                                {item.children.map((child) => (
                                  <Link
                                    key={child.label}
                                    href={publicUrl(child.href)}
                                    onClick={() => {
                                      setMobileOpen(false);
                                      setMobileOpenDropdown(null);
                                    }}
                                    className="px-3 py-2 text-sm font-medium text-brand-gray hover:text-brand-orange transition-colors"
                                  >
                                    {child.label}
                                  </Link>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={item.label}
                        href={publicUrl(item.href)}
                        onClick={() => setMobileOpen(false)}
                        className="block px-3 py-3 text-base font-medium text-brand-black border-b border-brand-gray-light/70 last:border-0"
                      >
                        {item.label}
                      </Link>
                    );
                  })}
                  <Link
                    href="/contact-us"
                    data-lead-popup
                    onClick={() => setMobileOpen(false)}
                    className="mt-4 rounded-full bg-brand-orange px-5 py-3 text-center text-sm font-semibold text-white"
                  >
                    Book Appointment
                  </Link>
                </nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}

